import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";
import { api, formatApiError, LOGO_URL } from "@/lib/api";

function Rules({ value }) {
    const rules = [
        { ok: value.length >= 8, label: "8+ caractères" },
        { ok: /[A-Z]/.test(value), label: "1 majuscule" },
        { ok: /\d/.test(value), label: "1 chiffre" },
        { ok: /[^A-Za-z0-9]/.test(value), label: "1 spécial" },
    ];
    return (
        <ul className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            {rules.map((r) => (
                <li key={r.label} className={r.ok ? "text-green-700" : "text-brand-smoke"}>
                    {r.ok ? "✓" : "○"} {r.label}
                </li>
            ))}
        </ul>
    );
}

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const navigate = useNavigate();
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const rulesOk = pwd.length >= 8 && /[A-Z]/.test(pwd) && /\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);
    const match = pwd.length > 0 && pwd === confirm;

    const submit = async (e) => {
        e.preventDefault();
        if (!rulesOk || !match || !token) return;
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6" data-testid="reset-password-invalid">
                <div className="w-full max-w-md rounded-2xl border border-brand-line bg-white p-10 text-center shadow-premium">
                    <h2 className="font-display text-3xl">Lien invalide</h2>
                    <p className="mt-4 text-brand-smoke">
                        Ce lien de réinitialisation n'est pas valide.{" "}
                        <Link to="/forgot-password" className="font-semibold text-brand-orange hover:underline">
                            Demander un nouveau lien
                        </Link>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6" data-testid="page-reset-password">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center justify-center gap-3">
                    <img src={LOGO_URL} alt="Cursus Physio" className="h-10 w-auto" />
                </Link>
                {done ? (
                    <div className="mt-10 rounded-2xl border border-brand-line bg-white p-10 text-center shadow-premium" data-testid="reset-password-done">
                        <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
                        <h2 className="mt-6 font-display text-3xl">Mot de passe mis à jour.</h2>
                        <p className="mt-3 text-brand-smoke">Vous allez être redirigé·e vers la page de connexion…</p>
                    </div>
                ) : (
                    <div className="mt-10 rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                        <h1 className="font-display text-3xl">Nouveau mot de passe.</h1>
                        <p className="mt-2 text-sm text-brand-smoke">Choisissez un mot de passe robuste. Ce lien n'est utilisable qu'une seule fois.</p>
                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider">Nouveau mot de passe</label>
                                <div className="relative mt-2">
                                    <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                                    <input
                                        type={show ? "text" : "password"}
                                        required
                                        value={pwd}
                                        onChange={(e) => setPwd(e.target.value)}
                                        data-testid="reset-password-pwd"
                                        className="w-full rounded-xl border border-brand-line bg-white py-3.5 pl-11 pr-12 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                    />
                                    <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-smoke" aria-label="Afficher">
                                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <Rules value={pwd} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider">Confirmer</label>
                                <input
                                    type={show ? "text" : "password"}
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="reset-password-confirm"
                                    className="mt-2 w-full rounded-xl border border-brand-line bg-white py-3.5 px-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                                {!match && confirm.length > 0 && (
                                    <div className="mt-2 text-xs text-red-600">Les mots de passe ne correspondent pas.</div>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={!rulesOk || !match || submitting}
                                data-testid="reset-password-submit"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-50"
                            >
                                {submitting ? "Mise à jour…" : "Mettre à jour mon mot de passe"}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
